import { set } from 'lodash'
import http from 'utils/http'

// User mutation imports.
import { SET_USER, SET_USER_PROPERTY, SET_USER_FILTER, RESET_USER_FILTERS, SET_USER_PROFILE_PROPERTY } from 'vx/mutations'

// User sub-modules.
import paymentDetail from './user/payment-detail'
import initialFiltersState from '../state/user/filters'

const modules = {
  paymentDetail
}

// User state.
const initialUserState = () => ({
  first_name: null,
  last_name: null,
  email: null,
  emailHash: null,
  phone: null,
  company: null,
  position: null,
  can_set_esports: null,
  status: 'pending',
  role: null,
  created_at: null,
  password: null,
  password_confirmation: null,
  custom_fields: null,
  date_of_birth: null,
  department_id: null,
  departments: null,
  profile: {
    gender: null,
    tagline: null,
    description: null,
    facebook_link: null,
    twitter_link: null,
    instagram_link: null,
    linkedin_link: null,
    tiktok_link: null,
    youtube_link: null
  },
  club_contact: null,
  based_in: { id: null },
  contracts: null,
  region: {
    id: null,
    name: null,
    timezone: null,
    currency_code: null,
    shortname: null
  },
  talent_opportunity_budgets: null
})

const state = {
  user: initialUserState(),
  filters: initialFiltersState()
}

// User state getters.
const getters = {
  /**
   * Determine if we have a user.
   *
   * @return bool
   */
  haveUser: (state) => {
    return state.user !== null && state.user.id !== null
  }
}

// User actions.
const actions = {
  /**
   * Upload a users profile image.
   *
   * @return Promise
   */
  uploadUserProfileImage ({ commit }, { id, file, cropPayload }) {
    let payload = new FormData()

    payload.append('profile_image', file)
    payload.append('profile_image_crop[x]', cropPayload.x)
    payload.append('profile_image_crop[y]', cropPayload.y)
    payload.append('profile_image_crop[width]', cropPayload.width)
    payload.append('profile_image_crop[height]', cropPayload.height)
    payload.append('profile_image_crop[scaleX]', cropPayload.scaleX)
    payload.append('profile_image_crop[scaleY]', cropPayload.scaleY)

    return http.post(`users/${id}/profile_image`, payload)
  },

  getUser ({ commit }, { id }) {
    return http.get(`users/${id}`)
      .then((response) => {
        commit(SET_USER, response.data)

        return response
      })
  },

  resetPassword ({ commit }, { email }) {
    return http.post(`reset/password`, { email })
      .then(response => { return response })
      .catch(error => { throw error })
  },

  updatePassword ({ commit }, payload) {
    return http.post(`reset/password/update`, payload)
      .then(response => { return response })
      .catch(error => { throw error })
  },

  resetUser ({ commit }) {
    commit(SET_USER, initialUserState())
  },

  /**
   * Update a user.
   *
   * @return Promise
   */
  updateUser ({ commit }, { id, payload }) {
    return http.put(`users/${id}`, payload).catch((error) => {
      return Promise.reject(error)
    })
  },

  /**
   * Update a user.
   *
   * @return Promise
   */
  updateUserCustomFields ({ commit }, { id, payload }) {
    return http.post(`users/${id}/custom_field`, payload).catch((error) => {
      return Promise.reject(error)
    })
  },
  /**
   * Restore a user.
   *
   * @return Promise
   */
  restoreUser ({ commit }, { id }) {
    return http.put(`users/${id}/restore`)
  },

  /**
   * Verify a users email address as an admin.
   *
   * @return Promise
   */
  verifyUser ({ dispatch }, { id }) {
    return http.put(`users/${id}/verify`)
      .then((response) => {
        dispatch('setUserProperty', { property: 'email_verified', value: response.data.email_verified })

        return response
      })
  },

  /**
   * Activate a user as an admin.
   *
   * @return Promise
   */
  activateUser ({ dispatch }, { id }) {
    return http.put(`users/${id}/activate`)
      .then((response) => {
        dispatch('setUserProperty', { property: 'status', value: response.data.status })

        return response
      })
  },

  deleteOnboardingVideo ({ dispatch }, { id }) {
    return http.delete(`onboarding/${id}`)
      .then((response) => {
        dispatch('setUserProfileProperty', { property: 'onboarding_video_link', value: null })

        return response
      })
  },

  deactivateUser ({ dispatch }, id) {
    return http
      .put(`users/${id}/deactivate`)
      .then((response) => {
        dispatch('setUserProperty', { property: 'status', value: response.data.status })

        return response
      })
  },

  /**
   * Resend the verification email.
   *
   * @return Promise
   */
  resendUserVerificationEmail ({ commit }, { id }) {
    return http.post(`users/${id}/resend_verification`)
  },

  sendActivationEmail ({ commit }, role) {
    return http.post(`user_activation/notify`, {role})
  },

  /**
   * Delete a user.
   *
   * @return Promise
   */
  deleteUser ({ commit }, { id }) {
    return http.delete(`users/${id}`)
  },

  requestDeletion ({ commit }, { id }) {
    return http.put(`users/${id}/request_deletion`)
  },

  /**
   * Permanently delete a user.
   *
   * @return Promise
   */
  permanentlyDeleteUser ({ commit }, { id }) {
    return http.delete(`users/${id}/permanently`)
  },

  /**
   * Update a users password.
   *
   * @return Promise
   */
  updateUserPassword (store, { id, payload }) {
    return http.put(`users/${id}/update_password`, payload)
  },

  updateTalentScores ({ store }, {id, payload}) {
    return http.post(`users/${id}/talent_scores`, payload)
  },

  saveMarketingOptIn ({ store }, { id, payload }) {
    return http.post(`users/${id}/marketing_opt_in`, payload)
  },

  updateDateTimePreferences ({ store }, {id, payload}) {
    return http.post(`users/${id}/date_time_options`, payload)
  },

  getAdminUsers () {
    return http.get('admin_users')
  },

  getTimezones () {
    return http.get('timezones')
  },

  setUserProperty ({ commit }, { property, value }) {
    commit(SET_USER_PROPERTY, { property, value })
  },

  setUserProfileProperty ({ commit }, { property, value }) {
    commit(SET_USER_PROFILE_PROPERTY, { property, value })
  },

  setFilter ({ commit }, { filter, value }) {
    commit(SET_USER_FILTER, { filter, value })
  },

  resetFilters ({ commit }) {
    commit(RESET_USER_FILTERS)
  }
}

// User state mutations.
const mutations = {
  [SET_USER] (state, user) {
    state.user = user
  },

  [SET_USER_PROPERTY] (state, { property, value }) {
    set(state.user, property, value)
  },

  [SET_USER_PROFILE_PROPERTY] (state, { property, value }) {
    set(state.user.profile, property, value)
  },

  [SET_USER_FILTER] (state, { filter, value }) {
    set(state.filters, filter, value)
  },

  [RESET_USER_FILTERS] (state) {
    state.filters = initialFiltersState()
  }
}

// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, modules, state, getters, actions, mutations
}
